import React, { Component } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Chart, Doughnut } from "react-chartjs-2";

import { GET, loading, NavbarAdmin, t } from "../../components/CustomComponent.js";
import { withRouter } from "../../components/withRouter.js";
import {
  BRAND,
  TYPE_RAMP,
  num,
  share,
  share1,
  money,
  moneyShort,
  fmtRange,
  fmtStamp,
  presetRange,
  toISODate,
  seriesLabel,
  localName,
  Figure,
  Bar as BarLine,
  Card,
  Section,
  CardTitle,
  KpiTile,
  PeriodFilter,
  ReportHero,
  PERIODS,
} from "../../components/report/ReportKit.js";

import "../../assets/css/dashboard.css";
import "../../assets/css/report.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  ArcElement,
  Filler,
  Tooltip
);
ChartJS.defaults.font.family = "FCIconic, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/* ============================================================================
 * รายงานการขายและรายได้  (/ad/reports/sales)
 *
 * ข้อมูลทั้งหน้ามาจาก GET admin/reports/sales?start=YYYY-MM-DD&end=YYYY-MM-DD
 *
 * "ดีล" ในระบบนี้คือทรัพย์ที่ปิดการขาย/ปล่อยเช่าได้ (status = sold | rented)
 * ซึ่งจะถูกประทับ closed_at พร้อมมูลค่าดีลและค่าคอมไว้ตอนที่สถานะเปลี่ยน
 *
 * หน้านี้เป็น "หน้าสรุป" ล้วน — ตอบคำถามระดับผลประกอบการ (ทำได้เท่าไร เทียบกับ
 * ช่วงก่อนหน้ายังไง มาจากทรัพย์ประเภทไหน ใครทำได้เท่าไร ตกหล่นตรงขั้นไหนของกรวย)
 * ไม่มีตารางดีลรายรายการ เพราะการไล่ดูทีละดีลเป็นงานคนละอย่างกัน และตัวเลขรวม
 * ด้านบนก็ตอบไม่ได้อยู่ดีว่าดีลไหนคือดีลไหน — เมนูทรัพย์สินที่กรองสถานะปิดการขาย
 * ทำได้ตรงกว่าและมีตัวกรองครบกว่า
 * ========================================================================== */

const FUNNEL_COLOR = ["#1a2e05", "#4d7c0f", "#84cc16", "#a3e635"];

/* โครงเปล่าที่ใช้ระหว่างรอ API ตอบ — ทุกส่วนของหน้าจึง render ได้โดยไม่ต้องเช็ก null ทีละจุด */
const EMPTY_DATA = {
  kpi: { deals: 0, sales: 0, commission: 0, avg_deal: 0, conversion: 0, avg_close_days: 0 },
  delta: { deals: 0, sales: 0, commission: 0, avg_deal: 0, conversion: 0, avg_close_days: 0 },
  funnel: [],
  chart: { unit: "day", points: [] },
  agents: [],
  revenue_by_type: [],
};

/* ========================================================================== */

class ReportSales extends Component {
  constructor(props) {
    super(props);
    const initial = presetRange("month");
    this.state = {
      period: "month",
      start: initial.start,
      end: initial.end,
      data: EMPTY_DATA,
      stamp: fmtStamp(),
    };
  }

  async componentDidMount() {
    await this.getData();
  }

  getData = async () => {
    const { start, end } = this.state;

    loading(true);
    const result = await GET("admin/reports/sales", {
      start: toISODate(start),
      end: toISODate(end || start),
    });
    loading(false);

    if (!result?.status || !result.data) return;

    // เติมคีย์ที่ขาดด้วยโครงเปล่า เผื่อ API เวอร์ชันเก่ายังไม่ส่งบางส่วนมา
    this.setState({ data: { ...EMPTY_DATA, ...result.data }, stamp: fmtStamp() });
  };

  changeRange = (next) => {
    // ยังเลือกวันสิ้นสุดไม่ครบ = ยังไม่ต้องยิงข้อมูลใหม่
    this.setState(next, () => (next.end ? this.getData() : null));
  };

  periodShort = () => {
    const p = PERIODS.find((x) => x.key === this.state.period);
    return p ? t(p.label) : t("custom_range");
  };

  rangeText = () => fmtRange(this.state.start, this.state.end);

  /* --------------------------------------------------------------- ส่วนย่อย */

  renderKpis() {
    const { kpi, delta } = this.state.data;
    const tiles = [
      { label: t("deals_closed"), value: <Figure value={kpi.deals} />, unit: t("unit_deals"), delta: delta.deals, icon: "" },
      { label: t("sales_value"), value: moneyShort(kpi.sales), delta: delta.sales, icon: "" },
      { label: t("commission"), value: moneyShort(kpi.commission), delta: delta.commission, icon: "" },
      { label: t("avg_deal_value"), value: moneyShort(kpi.avg_deal), delta: delta.avg_deal, icon: "" },
      { label: t("conversion_rate"), value: <Figure value={kpi.conversion} decimals={1} suffix="%" />, delta: delta.conversion, icon: "" },
      { label: t("avg_close_days"), value: <Figure value={kpi.avg_close_days} />, unit: t("unit_days"), delta: delta.avg_close_days, icon: "" },
    ];
    return (
      <div className="row g-3">
        {tiles.map((x, i) => (
          <div className="col-xl-2 col-lg-4 col-md-6 dash-in" key={i}>
            <KpiTile label={x.label} value={x.value} unit={x.unit} icon={x.icon} delta={x.delta} />
          </div>
        ))}
      </div>
    );
  }

  renderRevenueChart() {
    const { chart, kpi } = this.state.data;
    // API ส่งจุดกราฟมาเป็น [{ date, sales, commission }] พร้อมหน่วยของแกน
    // แล้วให้หน้าเว็บจัดป้ายกำกับเอง เพราะรูปแบบวันที่ขึ้นกับภาษาที่ผู้ใช้เลือก
    const points = chart.points || [];
    const labels = points.map((x) => seriesLabel(x.date, chart.unit));

    const data = {
      labels,
      datasets: [
        {
          type: "bar",
          label: t("sales_value"),
          data: points.map((x) => x.sales),
          backgroundColor: (ctx) => {
            const { chart: c } = ctx;
            if (!c.chartArea) return "rgba(163,230,53,.7)";
            const g = c.ctx.createLinearGradient(0, c.chartArea.bottom, 0, c.chartArea.top);
            g.addColorStop(0, "rgba(163,230,53,.42)");
            g.addColorStop(1, "rgba(77,124,15,.92)");
            return g;
          },
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 46,
          order: 2,
          yAxisID: "y",
        },
        {
          type: "line",
          label: t("commission"),
          data: points.map((x) => x.commission),
          borderColor: "#0891b2",
          borderWidth: 2.2,
          tension: 0.38,
          pointRadius: 3.4,
          pointBackgroundColor: "#fff",
          pointBorderColor: "#0891b2",
          pointBorderWidth: 1.8,
          pointHoverRadius: 5.5,
          pointHitRadius: 18,
          fill: false,
          order: 1,
          yAxisID: "y1",
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      animation: { duration: 900, easing: "easeOutQuart" },
      layout: { padding: { top: 10 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(20,32,22,.96)",
          padding: 12,
          cornerRadius: 12,
          boxPadding: 4,
          titleColor: "rgba(255,255,255,.6)",
          titleFont: { size: 11, weight: "normal" },
          bodyFont: { size: 13, weight: "bold" },
          callbacks: { label: (c) => ` ${c.dataset.label}  ${money(c.parsed.y)}` },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: BRAND.muted, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 16, padding: 6 },
        },
        y: {
          beginAtZero: true,
          position: "left",
          grid: { color: "rgba(20,32,22,.06)", drawTicks: false },
          border: { display: false },
          ticks: {
            color: BRAND.muted,
            font: { size: 10 },
            maxTicksLimit: 5,
            padding: 8,
            callback: (v) => (v >= 1000000 ? `${Math.round(v / 100000) / 10}${t("unit_million")}` : num(v)),
          },
        },
        y1: {
          beginAtZero: true,
          position: "right",
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: "#0891b2",
            font: { size: 10 },
            maxTicksLimit: 5,
            padding: 8,
            callback: (v) => (v >= 1000000 ? `${Math.round(v / 100000) / 10}${t("unit_million")}` : `${Math.round(v / 1000)}K`),
          },
        },
      },
    };

    return (
      <Card>
        <div className="p-4 d-flex flex-column h-100">
          <CardTitle
            title={t("revenue_trend")}
            note={`${this.periodShort()} · ${t("sales_vs_commission")}`}
            right={
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="d-flex align-items-center gap-2">
                  <span style={{ width: "10px", height: "10px", borderRadius: "3px", backgroundColor: "#4d7c0f" }} />
                  <span className="text-10 text-secondary">{t("sales_value")}</span>
                </span>
                <span className="d-flex align-items-center gap-2">
                  <span style={{ width: "14px", height: "2.5px", borderRadius: "999px", backgroundColor: "#0891b2" }} />
                  <span className="text-10 text-secondary">{t("commission")}</span>
                </span>
              </div>
            }
          />
          <div className="flex-grow-1" style={{ minHeight: "285px" }}>
            <Chart type="bar" data={data} options={options} />
          </div>
          <div className="dash-stat-row mt-3 pt-3">
            {[
              { label: t("sales_value"), value: moneyShort(kpi.sales), dot: "#4d7c0f" },
              { label: t("commission"), value: moneyShort(kpi.commission), dot: "#0891b2" },
              { label: t("commission_rate"), value: `${share1(kpi.commission, kpi.sales)}%`, dot: BRAND.ink },
            ].map((st, i) => (
              <div className="dash-stat" key={i}>
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: st.dot }} />
                  <span className="text-10 text-secondary">{st.label}</span>
                </div>
                <div className="fw-bold text-dark text-18 dash-figure mt-1">{st.value}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  renderFunnel() {
    const { funnel } = this.state.data;
    const top = funnel[0]?.value || 1;
    return (
      <Card>
        <div className="p-4 d-flex flex-column h-100">
          <CardTitle title={t("conversion_funnel")} note={t("funnel_desc")} />
          <div className="rpt-funnel">
            {funnel.map((f, i) => {
              const prev = i ? funnel[i - 1].value : null;
              const width = Math.max(share(f.value, top), 22);
              return (
                <div className="rpt-funnel__row" key={f.key}>
                  <div className="rpt-funnel__bar" style={{ width: `${width}%`, backgroundColor: FUNNEL_COLOR[i % FUNNEL_COLOR.length], color: i > 1 ? BRAND.ink : "#fff" }}>
                    <span className="text-truncate">{t(f.key)}</span>
                    <span className="dash-figure ms-2">{num(f.value)}</span>
                  </div>
                  {prev !== null && (
                    <div className="rpt-funnel__drop">
                      <span className="icon text-10">{""}</span>
                      {t("step_conversion")} {share1(f.value, prev)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-auto pt-3" style={{ borderTop: `1px solid ${BRAND.line}` }}>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-12 text-secondary">{t("overall_conversion")}</span>
              <span className="badge rounded-pill text-14 fw-bold px-3 py-2" style={{ backgroundColor: BRAND.limeSoft, color: BRAND.ink }}>
                {share1(funnel[funnel.length - 1]?.value || 0, top)}%
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  renderRevenueMix() {
    const { revenue_by_type } = this.state.data;
    const sorted = [...revenue_by_type].sort((a, b) => b.value - a.value);
    const total = sorted.reduce((s, x) => s + x.value, 0);

    const data = {
      labels: sorted.map((x) => localName(x)),
      datasets: [
        {
          data: sorted.map((x) => x.value),
          backgroundColor: sorted.map((_, i) => TYPE_RAMP[i % TYPE_RAMP.length]),
          borderColor: "#fff",
          borderWidth: 2,
          hoverOffset: 10,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "70%",
      animation: { animateRotate: true, duration: 900 },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: BRAND.ink,
          padding: 12,
          cornerRadius: 10,
          displayColors: false,
          titleFont: { size: 12, weight: "normal" },
          bodyFont: { size: 14, weight: "bold" },
          callbacks: { label: (c) => `${money(c.parsed)} (${share(c.parsed, total)}%)` },
        },
      },
    };

    return (
      <Card>
        <div className="p-4 d-flex flex-column h-100">
          <CardTitle title={t("revenue_by_type")} note={this.periodShort()} />
          <div className="position-relative mx-auto" style={{ width: "168px", height: "168px" }}>
            <Doughnut data={data} options={options} />
            <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ pointerEvents: "none" }}>
              <span className="fw-bold text-dark d-block dash-figure" style={{ fontSize: "20px" }}>
                {moneyShort(total)}
              </span>
              <div className="text-10 text-secondary">{t("total_income")}</div>
            </div>
          </div>
          <div className="mt-3">
            {sorted.map((x, i) => (
              <div className="dash-rank d-flex align-items-center justify-content-between py-1 px-2" key={x.name_th}>
                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                  <span className="rounded-circle me-2 flex-shrink-0" style={{ width: "9px", height: "9px", backgroundColor: TYPE_RAMP[i % TYPE_RAMP.length] }} />
                  <span className="text-12 text-dark text-truncate">{localName(x)}</span>
                </div>
                <div className="text-nowrap ms-2">
                  <span className="text-12 fw-bold text-dark dash-figure">{moneyShort(x.value)}</span>
                  <span className="text-10 text-secondary ms-2" style={{ display: "inline-block", width: "34px", textAlign: "right" }}>
                    {share(x.value, total)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  renderAgentTable() {
    const { agents } = this.state.data;
    const sum = agents.reduce(
      (a, r) => ({
        deals: a.deals + r.deals,
        sales: a.sales + r.sales,
        commission: a.commission + r.commission,
        target: a.target + (Number(r.target) || 0),
      }),
      { deals: 0, sales: 0, commission: 0, target: 0 }
    );

    return (
      <Card>
        <div className="p-4">
          {/* ตัวเลขในตารางนี้มาจาก dealsByUser(closed) ซึ่งกรองด้วยช่วงเวลาที่เลือกไว้ด้านบน
              ป้ายเดิมเขียนว่า "สะสมทั้งหมด" ซึ่งอ่านแล้วเข้าใจผิดว่าไม่ขึ้นกับตัวกรอง
              (คอลัมน์ "เป้า" ต่างหากที่เป็นค่าตั้งของทั้งปี — เขียนกำกับไว้ที่หัวคอลัมน์แทน) */}
          <CardTitle title={t("agent_performance")} note={this.periodShort()} />
          <div className="rpt-scroll">
            <table className="rpt-table">
              <thead>
                <tr>
                  <th style={{ width: "46px" }}>#</th>
                  <th style={{ minWidth: "190px" }}>{t("agent")}</th>
                  <th className="num">{t("deals_closed")}</th>
                  <th className="num">{t("sales_value")}</th>
                  <th className="num">{t("commission")}</th>
                  {/* เป้าเป็นค่าที่ตั้งไว้ให้ผู้ใช้รายนั้น (ทั้งปี) ไม่ได้ถูกหั่นตามช่วงที่กรอง
                      เหมือนคอลัมน์อื่น — กำกับไว้ที่หัวคอลัมน์เพราะเป็นข้อยกเว้นของตารางนี้ */}
                  <th className="num">
                    {t("target")}
                    <span className="text-10 text-secondary ms-1">({t("target_yearly")})</span>
                  </th>
                  <th style={{ minWidth: "160px" }}>{t("achievement")}</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a, i) => {
                  // ยังไม่ได้ตั้งเป้าให้คนนี้ = ไม่คิด % ความสำเร็จ ไม่งั้นจะอ่านเป็น 0% ทั้งที่ยังไม่มีเป้า
                  const hasTarget = Number(a.target) > 0;
                  const pct = hasTarget ? share(a.sales, a.target) : 0;
                  const hit = pct >= 100;
                  return (
                    <tr key={a.uid || a.name}>
                      <td>
                        <span className={`rpt-medal ${i === 0 ? "is-first" : i < 3 ? "is-top" : ""}`}>{i + 1}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-10 flex-shrink-0"
                            style={{ width: "30px", height: "30px", backgroundColor: BRAND.limeSoft, color: BRAND.ink }}
                          >
                            {a.initials}
                          </span>
                          <span className="fw-semibold">{a.name}</span>
                        </div>
                      </td>
                      <td className="num fw-bold">{num(a.deals)}</td>
                      <td className="num">{moneyShort(a.sales)}</td>
                      <td className="num" style={{ color: "#0891b2" }}>{moneyShort(a.commission)}</td>
                      <td className="num text-secondary">{hasTarget ? moneyShort(a.target) : "-"}</td>
                      <td>
                        {hasTarget ? (
                          <div className="d-flex align-items-center gap-2">
                            <div className="flex-grow-1">
                              <BarLine value={Math.min(pct, 100)} max={100} color={hit ? "#059669" : BRAND.lime} height={6} />
                            </div>
                            <span className="text-12 fw-bold dash-figure" style={{ minWidth: "44px", textAlign: "right", color: hit ? "#059669" : BRAND.ink }}>
                              {pct}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-12 text-secondary">{t("no_target")}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td />
                  <td>{t("total")}</td>
                  <td className="num">{num(sum.deals)}</td>
                  <td className="num">{moneyShort(sum.sales)}</td>
                  <td className="num">{moneyShort(sum.commission)}</td>
                  <td className="num">{sum.target ? moneyShort(sum.target) : "-"}</td>
                  <td className="num">{sum.target ? `${share(sum.sales, sum.target)}%` : "-"}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </Card>
    );
  }

  /* ---------------------------------------------------------------- render */

  render() {
    const { period, start, end, data, stamp } = this.state;
    const K = data.kpi;

    return (
      <div className="w-100 min-vh-100 dash-page">
        <NavbarAdmin />
        <div className="body p-4">
          <ReportHero
            icon={""}
            title={t("sales_report")}
            subtitle={t("sales_report_desc")}
            rangeText={this.rangeText()}
            headline={[
              { label: t("sales_value"), value: moneyShort(K.sales) },
              { label: t("commission"), value: moneyShort(K.commission) },
              { label: t("deals_closed"), value: num(K.deals), unit: t("unit_deals") },
            ]}
          />

          <div className="rpt-toolbar rpt-no-print">
            <PeriodFilter value={{ period, start, end }} onChange={this.changeRange} />
            <span className="text-12 text-secondary">
              <span className="icon me-2 text-12">{""}</span>
              {t("report_generated_at")} {stamp}
            </span>
          </div>

          <Section title={t("section_sales_overview")} note={`${this.periodShort()} · ${t("vs_previous_period")}`} />
          {this.renderKpis()}

          <Section title={t("section_revenue_trend")} />
          <div className="row g-3 pb-4">
            <div className="col-xl-8 col-lg-12 dash-in">{this.renderRevenueChart()}</div>
            <div className="col-xl-4 col-lg-6 dash-in">{this.renderFunnel()}</div>
            <div className="col-xl-4 col-lg-6 dash-in">{this.renderRevenueMix()}</div>
            <div className="col-xl-8 col-lg-12 dash-in">{this.renderAgentTable()}</div>
          </div>

          <div className="text-center text-10 text-secondary pb-4">{t("report_footer_note")}</div>
        </div>
      </div>
    );
  }
}

export default withRouter(ReportSales);
